import React, { useState } from "react";
import {
  Menu,
  Briefcase,
  Gem,
  ChevronRight,
  ChevronLeft,
  MessageSquare,
  RefreshCw, // Added this icon
} from "lucide-react";

// --- IMPORTS ---
// 1. Theme Logic
import { palette, styles, useMediaQuery } from "./theme";
// 2. The Data File
import { WEEKS_DATA } from "./data/weeksData";
// 3. Components
import Sidebar from "./components/Sidebar";
import Toggle from "./components/Toggle";
import MeetingAnalysisModal from "./components/MeetingAnalysisModal";
// 4. NEW TAB COMPONENT
import AdobeLearningTab from "./components/AdobeLearningTab";
import WeeklyPrepModal from "./components/WeeklyPrepModal"; // NEW IMPORT

export default function ExecDashboard() {
  const [tab, setTab] = useState("tracker");
  const [mode, setMode] = useState("work");
  const [weekIndex, setWeekIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [showPrep, setShowPrep] = useState(false); // NEW STATE
  const isMobile = useMediaQuery("(max-width: 768px)");

  // State for checkboxes
  const [checkboxState, setCheckboxState] = useState(() => {
    const initial = {};
    WEEKS_DATA.forEach((week) => {
      week.deliverables.forEach((d) => {
        if (d.defaultChecked) initial[d.id] = true;
      });
    });
    return initial;
  });

  const activeWeek = WEEKS_DATA[weekIndex];

  // Filter tasks based on mode
  const currentTasks = activeWeek.deliverables.filter((d) => d.type === mode);

  // Colors
  const activeColor = mode === "work" ? palette.bgSidebar : palette.gold;

  const toggleCheckbox = (id) => {
    setCheckboxState((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const nextWeek = () => {
    if (weekIndex < WEEKS_DATA.length - 1) setWeekIndex(weekIndex + 1);
  };

  const prevWeek = () => {
    if (weekIndex > 0) setWeekIndex(weekIndex - 1);
  };

  // Calculate stats for the visible card
  const completedCount = currentTasks.filter((d) => checkboxState[d.id]).length;
  const totalCount = currentTasks.length;
  const progress = totalCount === 0 ? 0 : (completedCount / totalCount) * 100;

  return (
    <div style={styles.container}>
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(true)}
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            zIndex: 30,
            background: "#FFFFFF",
            border: `1px solid ${palette.line}`,
            borderRadius: "8px",
            padding: "8px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
          }}
        >
          <Menu size={20} color={palette.text} />
        </button>
      )}

      <Sidebar
        isMobile={isMobile}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        tab={tab}
        setTab={setTab}
      />

      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* CONDITIONAL RENDERING: SHOW ADOBE TAB OR WEEKLY PLANNER */}
        {tab === "adobe" ? (
          <AdobeLearningTab />
        ) : (
          <>
            {/* TOP TAB SWITCHER (Only for Weekly Planner) */}
            <div
              style={{
                display: "flex",
                backgroundColor: "#FFFFFF",
                borderBottom: `1px solid ${palette.line}`,
              }}
            >
              <div
                onClick={() => setMode("work")}
                style={styles.tabBtn(mode === "work", "work")}
              >
                <Briefcase
                  size={16}
                  style={{
                    display: "inline",
                    marginRight: 8,
                    marginBottom: -2,
                  }}
                />
                Professional (80%)
              </div>
              <div
                onClick={() => setMode("life")}
                style={styles.tabBtn(mode === "life", "life")}
              >
                <Gem
                  size={16}
                  style={{
                    display: "inline",
                    marginRight: 8,
                    marginBottom: -2,
                  }}
                />
                Lifestyle (20%)
              </div>
            </div>

            {/* MAIN CONTENT AREA (Weekly Planner) */}
            <section
              style={{
                flex: 1,
                padding: isMobile ? "20px" : "40px 60px",
                overflowY: "auto",
                backgroundColor: palette.bg,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* WEEK NAVIGATOR */}
              <div
                style={{
                  width: "100%",
                  maxWidth: "800px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "32px",
                }}
              >
                <button
                  onClick={prevWeek}
                  disabled={weekIndex === 0}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: weekIndex === 0 ? "not-allowed" : "pointer",
                    opacity: weekIndex === 0 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: palette.textSoft,
                  }}
                >
                  <ChevronLeft size={20} /> Previous
                </button>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: activeColor,
                      textTransform: "uppercase",
                      letterSpacing: "0.15em",
                      marginBottom: "4px",
                    }}
                  >
                    Week of
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: palette.text,
                    }}
                  >
                    {activeWeek.date}
                  </div>
                </div>
                <button
                  onClick={nextWeek}
                  disabled={weekIndex === WEEKS_DATA.length - 1}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor:
                      weekIndex === WEEKS_DATA.length - 1
                        ? "not-allowed"
                        : "pointer",
                    opacity: weekIndex === WEEKS_DATA.length - 1 ? 0.3 : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: palette.textSoft,
                  }}
                >
                  Next <ChevronRight size={20} />
                </button>
              </div>

              {/* SINGLE WEEK CARD VIEW */}
              <div
                style={{
                  width: "100%",
                  maxWidth: "800px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "16px",
                  border: `1px solid ${palette.line}`,
                  boxShadow: "0 20px 40px -5px rgba(0, 0, 0, 0.05)",
                  overflow: "hidden",
                }}
              >
                {/* CARD HEADER */}
                <div
                  style={{
                    padding: "40px",
                    borderBottom: `1px solid ${palette.line}`,
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <h1
                        style={{
                          fontSize: "32px",
                          fontWeight: 700,
                          color: palette.text,
                          margin: "0 0 16px 0",
                          fontFamily: "serif",
                        }}
                      >
                        {activeWeek.title}
                      </h1>
                      <p
                        style={{
                          fontSize: "16px",
                          color: palette.textSoft,
                          lineHeight: "1.6",
                          maxWidth: "600px",
                        }}
                      >
                        {activeWeek.summary}
                      </p>
                    </div>
                    {!isMobile && (
                      <div style={{ display: "flex", gap: "8px" }}>
                        {activeWeek.tags.map((t) => (
                          <span
                            key={t}
                            style={{
                              fontSize: "11px",
                              color: activeColor,
                              background:
                                mode === "work" ? "#F1F5F9" : palette.goldLight,
                              padding: "6px 12px",
                              borderRadius: "99px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* MEETING INTELLIGENCE CARD */}
                  {mode === "work" &&
                    activeWeek.meetings &&
                    activeWeek.meetings.length > 0 && (
                      <div
                        onClick={() =>
                          setActiveMeeting(activeWeek.meetings[0].id)
                        }
                        style={{
                          marginTop: "32px",
                          padding: "20px",
                          background: "#F0F9FF",
                          border: `1px solid ${palette.navyLight}40`,
                          borderRadius: "12px",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: palette.navyLight,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              marginBottom: 6,
                            }}
                          >
                            Meeting Intelligence • {activeWeek.meetings[0].date}
                          </div>
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: 700,
                              color: palette.bgSidebar,
                            }}
                          >
                            {activeWeek.meetings[0].title}
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: palette.textSoft,
                            }}
                          >
                            With {activeWeek.meetings[0].with} • Analysis Ready
                          </div>
                        </div>
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <MessageSquare size={16} color={palette.navyLight} />
                        </div>
                      </div>
                    )}

                  {/* --- WEEKLY PREP BUTTON (NEW) --- */}
                  {mode === "work" && (
                    <div
                      onClick={() => setShowPrep(true)}
                      style={{
                        marginTop: "16px",
                        padding: "16px 20px",
                        background: "#FFFFFF",
                        border: `1px dashed ${palette.line}`,
                        borderRadius: "12px",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = palette.gold;
                        e.currentTarget.style.background = "#FFFCF5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = palette.line;
                        e.currentTarget.style.background = "#FFFFFF";
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: palette.goldLight,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <RefreshCw size={16} color={palette.gold} />
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: palette.text,
                            }}
                          >
                            Generate Weekly 1:1 Recap
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: palette.textSoft,
                            }}
                          >
                            Pre-written updates based on this week's progress
                          </div>
                        </div>
                      </div>
                      <ChevronRight size={16} color={palette.textSoft} />
                    </div>
                  )}

                  {/* PROGRESS BAR */}
                  <div style={{ marginTop: "32px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: palette.textSoft,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Weekly Completion
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: 700,
                          color: activeColor,
                        }}
                      >
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: "6px",
                        backgroundColor: palette.line,
                        borderRadius: "3px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${progress}%`,
                          height: "100%",
                          backgroundColor: activeColor,
                          transition:
                            "width 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* TASKS LIST */}
                <div
                  style={{
                    padding: "24px 40px 40px 40px",
                    backgroundColor: "#F9FAFB",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    {currentTasks.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: palette.textSoft,
                          fontStyle: "italic",
                        }}
                      >
                        No {mode} items planned for this week. Enjoy the
                        balance.
                      </div>
                    )}

                    {currentTasks.map((item) => {
                      const isChecked = checkboxState[item.id];
                      return (
                        <div
                          key={item.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "20px",
                            borderRadius: "12px",
                            backgroundColor: "#FFFFFF",
                            border: isChecked
                              ? `1px solid ${activeColor}`
                              : `1px solid ${palette.line}`,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                            transition: "all 0.2s",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "16px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: 500,
                                color: isChecked
                                  ? palette.textSoft
                                  : palette.text,
                                textDecoration: isChecked
                                  ? "line-through"
                                  : "none",
                                transition: "color 0.2s",
                              }}
                            >
                              {item.text}
                            </div>
                          </div>
                          <Toggle
                            checked={isChecked}
                            onChange={() => toggleCheckbox(item.id)}
                            color={activeColor}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "24px",
                  color: palette.textSoft,
                  fontSize: "13px",
                  fontStyle: "italic",
                }}
              >
                Viewing Week {weekIndex + 1} of {WEEKS_DATA.length}
              </div>
            </section>
          </>
        )}
      </main>

      {activeMeeting && (
        <MeetingAnalysisModal onClose={() => setActiveMeeting(null)} />
      )}
      {showPrep && (
        <WeeklyPrepModal
          onClose={() => setShowPrep(false)}
          activeWeek={activeWeek}
        />
      )}
    </div>
  );
}
